<template>
  <div class="sidebar-header">
    <!-- 재화 compact — 헤더 우상단. 클릭 시 당근 다이얼로그. 접힘 모드에선 숨김. -->
    <div
      v-if="!isMini"
      class="sidebar-header__currency"
      :title="$t('nav.currencyStore')"
      @click="showCurrencyDialog = true"
    >
      <app-currency-count :balance="currencyBalance" />
    </div>

    <v-list-item class="pt-7 pb-2 d-flex justify-center">
      <!-- 펫 이미지(중심) + 유저 프로필 좌상단 살짝 겹침 → "내 펫" 느낌 -->
      <div class="avatar-combo" :class="{ mini: isMini }" @click="openCareDialog" :title="virtupetName">
        <v-avatar
          class="avatar-combo__pet"
          :size="isMini ? 57 : 92"
          :title="virtupetName"
          :style="petBgColor ? { background: petBgColor } : null"
        >
          <v-img v-if="petImageUrl" :src="petImageUrl" contain alt="pet" />
          <v-img v-else :src="userImageUrl" @error="onImageError" alt="user" />
        </v-avatar>
        <!-- 펫 상태 — 펫 우하단에 작게 -->
        <span v-if="petImageUrl" class="avatar-combo__status" :title="petStatus.label">{{ petStatus.emoji }}</span>
        <!-- 유저 프로필 — 좌상단 -->
        <v-avatar v-if="petImageUrl" class="avatar-combo__user" :size="isMini ? 32 : 46">
          <v-img :src="userImageUrl" @error="onImageError" alt="user" />
        </v-avatar>
      </div>
    </v-list-item>

    <v-list-item>
      <v-list-item-content>
        <v-list-item-title class="text-h6 text-center d-flex justify-center">
          <div class="the-left-bar__title-font d-flex align-end" style="font-size: 24px">
            <span>{{ userName || "버츄" }}</span>
            <span class="mr-2" style="font-size: 0.8em">의 </span>
            <span class="primary--text" style="font-weight: bold">{{ virtupetName || "펫" }}</span>
          </div>
        </v-list-item-title>
      </v-list-item-content>
    </v-list-item>

    <!-- 펫 4 바이탈 패널 (🍖배고픔 ⚡에너지 / 기분 💤졸림) — features/2(성장). stage1 릴리즈 동안 숨김(코드 유지). -->
    <pet-vital-panel v-if="false" class="px-4 pb-2" :meters="meters" :mood-emoji="petStatus.emoji" />

    <!-- 펫 케어 다이얼로그 (레트로 다마고치 기기) — features/2(성장). stage1 릴리즈 동안 숨김(코드 유지). -->
    <pet-care-dialog v-if="false" v-model="showCareDialog" />
    <currency-dialog v-model="showCurrencyDialog" />
  </div>
</template>

<script>
import { mapState } from "vuex";
import SupabaseApi from "@/api/SupabaseApi";
import { MODEL_THUMB_COLOR } from "@/constants";
import { resolveMood } from "@/constants/petMood";
import PetCareDialog from "@/features/2/PetCareDialog.vue";
import PetVitalPanel from "@/features/2/PetVitalPanel.vue";

export default {
  name: "SidebarHeader",
  components: { PetCareDialog, PetVitalPanel },
  props: {
    isMini: { type: Boolean, default: false },
  },
  created() {
    // 인증 컨텍스트가 준비된 경우에만 즉시 로드(이미 로그인된 상태로 마운트되는 경우).
    // 로그인 부트스트랩 중이면 _authUserId 미설정 → 조기 호출 시 AUTH_USER_ID_NOT_FOUND 에러.
    // 이 경우는 App.vue가 인증 완료 후 petRevision bump + loadCurrencyBalance를 발사하므로 거기서 로드됨.
    if (SupabaseApi.getAuthUserId()) {
      this.$store.dispatch("loadCurrencyBalance");
      this.loadPetSummary();
    }
  },
  data() {
    return {
      petImageUrlRaw: null, // pet_profile.image_url (없으면 model 썸네일 폴백)
      petModelIndex: null,
      petBgColor: null, // 펫 이미지에서 추출한 부드러운 배경 틴트 (없으면 CSS #fff)
      moodMap: [], // ssot.PET_MOOD_MAP 버킷 배열
      // 라이브 미터 (배고픔/에너지/기분/졸림). 모두 high=good 방향.
      meters: {
        hunger: 0,
        hungerMax: 100,
        energy: 0,
        energyMax: 100,
        mood: 0,
        moodMax: 100,
        awake: 0,
        awakeMax: 100,
      },
      // 펫 케어 다이얼로그 표시
      showCareDialog: false,
      showCurrencyDialog: false,
    };
  },
  watch: {
    // 펫 변경 시 사이드바 요약(이미지/상태/미터/이름) 재로드
    petRevision() {
      this.loadPetSummary();
    },
    // 펫 이미지 바뀌면 배경 틴트 갱신 (썸네일=상수 / 커스텀=런타임 추출)
    petImageUrl: {
      immediate: true,
      handler() {
        this.updatePetBgColor();
      },
    },
  },
  computed: {
    ...mapState(["userName", "userImageUrl", "virtupetName", "currencyBalance", "petRevision"]),
    // model_url에서 동물 key 추출. 두 형식 혼재: 깨끗한 key("fox") 또는 GLB 경로("models/v5/fox_v5+...glb").
    petModelKey() {
      const url = this.petModelIndex;
      if (!url) return null;
      const file = url
        .split("/")
        .pop()
        .replace(/\.(glb|gltf)$/i, ""); // fox_v5+240415_142749
      return file.split("+")[0].split("_v")[0] || null; // fox (dog2_v5 → dog2)
    },
    // 펫 이미지: pet_profile.image_url 우선, 없으면 동물 key 썸네일 폴백(trial 격리 폴더).
    petImageUrl() {
      if (this.petImageUrlRaw) return this.petImageUrlRaw;
      return this.petModelKey ? `/pet/profile-thumbnail-fallback/${this.petModelKey}_thumbnail.png` : null;
    },
    // mood_meter 수치 → ssot.PET_MOOD_MAP 버킷 변환 → { id, label, emoji }. 없으면 ❔ 폴백.
    // (아바타 배지 + PetVitalPanel 기분 바 아이콘에 사용)
    petStatus() {
      return resolveMood(this.meters.mood, this.meters.moodMax, this.moodMap);
    },
  },
  methods: {
    // 평균색(r,g,b) → 흰색과 두 비율로 섞은 라디얼 그라데이션(중앙 연→가장자리 진).
    gradientFrom(r, g, b) {
      const mix = (c, ratio) => Math.round(c * ratio + 255 * (1 - ratio));
      const tone = (ratio) => `rgb(${mix(r, ratio)}, ${mix(g, ratio)}, ${mix(b, ratio)})`;
      return `radial-gradient(circle at 50% 35%, ${tone(0.12)} 0%, ${tone(0.95)} 100%)`;
    },
    // 배경 틴트 갱신: 썸네일이면 사전계산 상수색 사용, 커스텀 image_url만 런타임 canvas 추출.
    updatePetBgColor() {
      this.petBgColor = null;
      // 1) 썸네일 → 빌드타임 상수색 (canvas 불필요, CORS 무관)
      if (!this.petImageUrlRaw && this.petModelKey && MODEL_THUMB_COLOR[this.petModelKey]) {
        const hex = MODEL_THUMB_COLOR[this.petModelKey];
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        this.petBgColor = this.gradientFrom(r, g, b);
        return;
      }
      // 2) 커스텀 image_url → 런타임 추출 (외부 CORS 실패 시 #fff 폴백)
      const url = this.petImageUrl;
      if (!url) return;
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const W = 24;
          const canvas = document.createElement("canvas");
          canvas.width = W;
          canvas.height = W;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, W, W);
          const data = ctx.getImageData(0, 0, W, W).data;
          let r = 0;
          let g = 0;
          let b = 0;
          let n = 0;
          for (let i = 0; i < data.length; i += 4) {
            if (data[i + 3] < 125) continue; // 투명 픽셀 제외
            r += data[i];
            g += data[i + 1];
            b += data[i + 2];
            n += 1;
          }
          if (!n) return;
          this.petBgColor = this.gradientFrom(r / n, g / n, b / n);
        } catch (e) {
          this.petBgColor = null;
        }
      };
      img.onerror = () => {
        this.petBgColor = null;
      };
      // 같은 URL이 다른 곳(아바타 <img>)에서 crossorigin 없이 먼저 캐시되면
      // CORS 캐시 오염으로 getImageData가 차단됨. 쿼리로 CORS 전용 캐시 엔트리를 분리.
      img.src = url + (url.includes("?") ? "&" : "?") + "cors=1";
    },
    // 활성 펫 요약(아바타 이미지 + 상태 + 미터 + 이름) 로드. 펫 변경 시 재호출.
    async loadPetSummary() {
      try {
        const [profile, moodMap, meters] = await Promise.all([
          SupabaseApi.PetProfile.fetchProfile(),
          SupabaseApi.PetStatus.fetchMoodMap(),
          SupabaseApi.PetStatus.fetchMyMeters(),
        ]);
        this.petImageUrlRaw = profile && profile.imageUrl;
        this.petModelIndex = profile && profile.modelUrl;
        this.moodMap = moodMap;
        this.meters = meters;
        if (profile && profile.name) this.$store.commit("__setVirtupetName", profile.name);
      } catch (error) {
        console.error(error);
      }
    },
    onImageError() {
      this.$store.commit("__setUserImageUrl", "/temp/profile_fallback_image.png");
    },
    // 펫 아바타 클릭 → 레트로 다마고치 케어 다이얼로그 (이전엔 /pet/select로 이동)
    openCareDialog() {
      this.showCareDialog = true;
    },
  },
};
</script>

<style scoped>
/* 4 바이탈 패널 → features/2/PetVitalPanel.vue, 재화 칩 → components/AppCurrencyPill.vue 로 분리됨 */

/* 재화 compact — 헤더 우상단 */
.sidebar-header {
  position: relative;
}
.sidebar-header__currency {
  position: absolute;
  top: 6px;
  right: 12px;
  z-index: 1;
  cursor: pointer;
}

/* 펫 아바타(중심) + 유저 아바타 좌상단 겹침 */
.avatar-combo {
  position: relative;
  display: inline-block;
  line-height: 0;
  cursor: pointer;
}
/* 중심: 펫 이미지 */
.avatar-combo__pet {
  background: #fff;
}
/* 유저 프로필 — 좌상단 겹침 */
.avatar-combo__user {
  position: absolute;
  left: -16px;
  top: -14px;
  background: #fff;
  border: 2px solid #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  animation: avatar-float 3.4s ease-in-out infinite;
}
.avatar-combo.mini .avatar-combo__user {
  left: -10px;
  top: -9px;
  border-width: 1.5px;
}
/* 펫 상태 이모지 — 펫 이미지 우하단 모서리에 작게 */
.avatar-combo__status {
  position: absolute;
  right: -2px;
  bottom: -2px;
  width: 23px;
  height: 23px;
  border-radius: 50%;
  background: #fff;
  border: 1.5px solid #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  line-height: 1;
}
.avatar-combo.mini .avatar-combo__status {
  width: 18px;
  height: 18px;
  font-size: 11px;
  right: -3px;
  bottom: -2px;
}

/* 둥둥 떠다니는 모션 */
@keyframes avatar-float {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-3px);
  }
}
@media (prefers-reduced-motion: reduce) {
  .avatar-combo__user {
    animation: none;
  }
}
</style>
